'use client'
import classNames from 'classnames'
import { useParams } from 'next/navigation'
import type { FC } from 'react'
import styles from './TopPartnersContainer.module.scss'
import { TopPartnersContainerSkeleton } from '@/_pages/landing-pages/ui/components/top-partners-container/top-partners-container-skeleton/TopPartnersContainerSkeleton'
import type { TopPartner } from '@/country/types'
import { Translator } from '@/tools/i18n'
import { useLocalisation } from '@/tools/i18n/client'
import { getImageUrl } from '@/tools/image-service'
import { Blob01, CoverBlob, HighlightText, Tag, TagStyle, TagVariant } from '@/tools/ui'
import { useDeviceMedia } from '@/tools/ui'

type Props = {
  topPartners: Array<TopPartner>
  isLoading: boolean
}

export const TopPartnersContainer: FC<Props> = ({ topPartners, isLoading }) => {
  const titleStyles = classNames(styles.title, 'pintxo-typography-feature3')
  const imageUrl = (img: string) => getImageUrl({ source: img })
  const { t } = useLocalisation()
  const { country } = useParams()
  const countryCode = typeof country === 'string' ? country.toUpperCase() : country
  const { isMobile } = useDeviceMedia()
  const place = country ? t(`common.country.${countryCode}`) : 'Glovo'
  const tagStyle = isMobile ? TagStyle.neutral : TagStyle.promotion

  return (
    <article className={styles.wrapper}>
      <p className={titleStyles}>
        <Translator
          keyText="top-brands.title"
          params={{
            topRestaurants: (
              <HighlightText
                size={'small'}
                text={t('top-brands.top-restaurants')}
                textClass="pintxo-typography-feature3"
              />
            ),
            place: place,
          }}
        />
      </p>

      {isLoading ? (
        <TopPartnersContainerSkeleton />
      ) : (
        <section className={styles.partnersContainer}>
          {topPartners.map((partner) => (
            <div key={partner.title} className={styles.partnerWrapper}>
              <div className={styles.blobWrapper}>
                <CoverBlob
                  alt={partner.title}
                  className={styles.blobImage}
                  shape={Blob01}
                  src={imageUrl(partner.imageId)}
                />
                <Tag
                  className={styles.partnerName}
                  label={partner.title}
                  style={tagStyle}
                  variant={TagVariant.secondary}
                />
              </div>
            </div>
          ))}
        </section>
      )}
      <div className={styles.curve}></div>
    </article>
  )
}
